import './accordion-plus.scss'
import { Icon } from '@vista-digital/occ-ui'
import type { Ref, SyntheticEvent } from 'react'
import { forwardRef, useImperativeHandle } from 'react'
import { cx } from '../../../utils/classNames'
import { useAccordionAnimations } from './hooks/useAccordionAnimations'
import { useTrackIcon } from './hooks/useTrackIcon'
import type {
    AccordionPlusBaseProps,
    AccordionPlusPropsWithIcon,
    AccordionPlusPropsWithOpenCloseIcon,
} from './types/AccordionPlusBaseProps'
import type { AccordionPlusHandles } from './types/AccordionPlusHandles'

export type AccordionPlusProps = AccordionPlusBaseProps &
    (AccordionPlusPropsWithIcon | AccordionPlusPropsWithOpenCloseIcon)

export const AccordionPlus = forwardRef((props: AccordionPlusProps, ref: Ref<AccordionPlusHandles>) => {
    const { header, open = false, children, transitionDuration = 0.07, className } = props

    const transitionDelay = 0
    const icon = useTrackIcon(props)
    const { detailRef, classMode, ...events } = useAccordionAnimations(transitionDuration, transitionDelay, open)
    const cls = cx('accordion-plus', classMode, className)

    const { closeAccordion, toggleAccordion, openAccordion } = events
    useImperativeHandle(ref, () => ({ open: openAccordion, close: closeAccordion, toggle: toggleAccordion }))
    const handleOnClick = (e: SyntheticEvent) => {
        e.preventDefault()
        toggleAccordion()
    }

    if (!header || !children) return null

    return (
        <details ref={detailRef} className={cls} role="group">
            <summary role="button" className="accordion-plus__header" onClick={handleOnClick}>
                {header}
                {icon.name && (
                    <span className={icon.class} style={{ transition: `${transitionDuration}s` }}>
                        <Icon name={icon.name} />
                    </span>
                )}
            </summary>
            <div className={'accordion-plus__content'}>{children}</div>
        </details>
    )
})
